import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';

import { AsyncDisplay, LoadingMessage } from 'js/components';
import AssessmentHistoryRadialPieChart from 'js/engines/SurveyAssessment/components/Review/AssessmentHistoryRadialPieChart';
import { getCategoryLookups } from 'js/utils/question-set';

import { scoreNode } from '../OrgRecommendations/utils';
import { buildOrgHistoryDataMap } from './buildOrgHistoryDataMap';
import { flattenOrgPickerRows } from 'js/components/OrgNodePicker/flattenOrgPickerRows';
import OrgNodePicker from 'js/components/OrgNodePicker/OrgNodePicker';

import styles from './OrgAssessmentHistory.module.scss';

function OrgAssessmentHistoryChart({
  organization,
  assessmentTemplate,
  questionScores,
  catalog = null,
  loading = false,
  error = null,
}) {
  const pickerRows = useMemo(
    () => flattenOrgPickerRows(organization),
    [organization]
  );

  const [selectedRow, setSelectedRow] = useState(null);

  useEffect(() => {
    if (!pickerRows.length) {
      setSelectedRow(null);
      return;
    }

    setSelectedRow((current) => {
      if (current && pickerRows.some((row) => row.rowKey === current.rowKey)) {
        return current;
      }

      return pickerRows[0];
    });
  }, [pickerRows]);

  const scoredNode = useMemo(() => {
    if (!selectedRow || !questionScores) {
      return null;
    }

    return scoreNode(selectedRow.node, questionScores, organization);
  }, [selectedRow, questionScores, organization]);

  const dataMap = useMemo(() => {
    if (!scoredNode) {
      return null;
    }
    if (!assessmentTemplate && !catalog) {
      return null;
    }

    return buildOrgHistoryDataMap(assessmentTemplate, scoredNode, catalog);
  }, [scoredNode, assessmentTemplate, catalog]);

  const categoryLookups = useMemo(() => {
    if (!dataMap) {
      return new Map();
    }

    return getCategoryLookups(
      [{ config: assessmentTemplate?.config }],
      dataMap.categories
    );
  }, [dataMap, assessmentTemplate?.config]);

  const categoryColorMap = useMemo(() => {
    const map = {};
    categoryLookups.forEach((value, key) => {
      map[key] = value.palette;
    });
    return map;
  }, [categoryLookups]);

  return (
    <div className={styles.root}>
      <div className={classNames('manage-org', styles['picker-shell'])}>
        <OrgNodePicker
          rows={pickerRows}
          selectedRow={selectedRow}
          onSelect={setSelectedRow}
          disabled={loading || !organization}
        />
      </div>
      <AsyncDisplay isLoading={loading} isError={!!error}>
        <AsyncDisplay.Loading>
          <LoadingMessage iconSize="2x">Loading data&hellip;</LoadingMessage>
        </AsyncDisplay.Loading>
        <AsyncDisplay.Error>
          <div className="danger">
            Uh oh, looks like there was a problem loading the data
          </div>
        </AsyncDisplay.Error>
        <AsyncDisplay.Resolved>
          {dataMap ? (
            <AssessmentHistoryRadialPieChart
              dataMap={dataMap}
              categoryLookups={categoryLookups}
              categoryColorMap={categoryColorMap}
            />
          ) : null}
        </AsyncDisplay.Resolved>
      </AsyncDisplay>
    </div>
  );
}

export default OrgAssessmentHistoryChart;
